import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const DAY_COLORS = ["#0F1C2E", "#C48B7F", "#3B6978", "#87988A", "#E8A598", "#1B2C44", "#8A6D3B"];
const BERMUDA_CENTER = [32.313, -64.762];

export const dayColor = (day) => DAY_COLORS[(day - 1) % DAY_COLORS.length];

const pinIcon = (day, order) =>
  L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;border-radius:9999px;background:${dayColor(day)};color:#FAF8F5;display:flex;align-items:center;justify-content:center;font:700 11px Arial,sans-serif;border:2px solid #FAF8F5;box-shadow:0 1px 5px rgba(15,28,46,.45)">${order}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13);
      return;
    }
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])).pad(0.18));
  }, [map, points]);
  return null;
};

export const TripMap = ({ itinerary, saved }) => {
  const [dayFilter, setDayFilter] = useState(0); // 0 = all days
  const recById = useMemo(() => Object.fromEntries((saved || []).map((r) => [r.id, r])), [saved]);

  const daysWithPoints = useMemo(
    () =>
      itinerary
        .map((d) => {
          const pts = [];
          d.slots.forEach((s) => {
            if (!s.recId) return;
            const rec = recById[s.recId];
            if (rec && rec.lat != null && rec.lng != null) {
              pts.push({ day: d.day, time: s.time, label: s.label, lat: rec.lat, lng: rec.lng });
            }
          });
          return { day: d.day, points: pts.map((p, i) => ({ ...p, order: i + 1 })) };
        })
        .filter((d) => d.points.length > 0),
    [itinerary, recById]
  );

  if (daysWithPoints.length === 0) {
    return (
      <p className="border border-dashed border-navy/20 bg-sand-light/60 p-8 text-center text-sm text-navy/55" data-testid="trip-map-empty">
        Your saved places don't have map locations yet — they'll appear here as we add them.
      </p>
    );
  }

  const visible = dayFilter === 0 ? daysWithPoints : daysWithPoints.filter((d) => d.day === dayFilter);
  const allPoints = visible.flatMap((d) => d.points);

  return (
    <div data-testid="trip-map">
      <div className="mb-3 flex flex-wrap gap-1.5" data-testid="trip-map-days">
        <button
          onClick={() => setDayFilter(0)}
          data-testid="trip-map-day-all"
          className={`border px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider transition-colors ${dayFilter === 0 ? "border-navy bg-navy text-ivory" : "border-navy/15 text-navy/50 hover:border-navy/40"}`}
        >
          All Days
        </button>
        {daysWithPoints.map((d) => (
          <button
            key={d.day}
            onClick={() => setDayFilter(d.day)}
            data-testid={`trip-map-day-${d.day}`}
            className={`border px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider transition-colors ${dayFilter === d.day ? "border-navy text-ivory" : "border-navy/15 text-navy/50 hover:border-navy/40"}`}
            style={dayFilter === d.day ? { background: dayColor(d.day), borderColor: dayColor(d.day) } : {}}
          >
            Day {d.day}
          </button>
        ))}
      </div>
      <div className="relative z-0 overflow-hidden border border-navy/10" data-testid="trip-map-canvas">
        <MapContainer center={BERMUDA_CENTER} zoom={11} scrollWheelZoom={false} style={{ height: 420, width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={allPoints} />
          {visible.map((d) =>
            d.points.length > 1 ? (
              <Polyline
                key={`line-${d.day}`}
                positions={d.points.map((p) => [p.lat, p.lng])}
                pathOptions={{ color: dayColor(d.day), weight: 3, opacity: 0.75, dashArray: dayFilter === 0 ? "6 8" : undefined }}
              />
            ) : null
          )}
          {visible.flatMap((d) =>
            d.points.map((p) => (
              <Marker key={`${d.day}-${p.order}`} position={[p.lat, p.lng]} icon={pinIcon(d.day, p.order)}>
                <Popup>
                  <p style={{ margin: 0, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#5C6B73" }}>
                    Day {p.day} · {p.time}
                  </p>
                  <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 600, color: "#0F1C2E" }}>{p.label}</p>
                </Popup>
              </Marker>
            ))
          )}
        </MapContainer>
      </div>
      <p className="mt-3 text-[11px] text-navy/45">
        Pins are numbered in the order they appear on each day; the dashed line traces the day's route. Experience pins mark the usual departure area.
      </p>
    </div>
  );
};
